import { Link } from "react-router-dom";
import {
  Heart,
  Sparkles,
  Feather,
  Compass,
  Users,
  Wand2,
  Flower,
  ArrowRight,
  Mail,
  Phone,
  MapPin,
  Quote,
  ChevronRight,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import site from "@/data/site";

const iconMap = {
  heart: Heart,
  sparkles: Sparkles,
  feather: Feather,
  compass: Compass,
  users: Users,
  wand: Wand2,
  flower: Flower,
};

function Hero() {
  return (
    <section
      className="relative overflow-hidden bg-bloom"
      aria-labelledby="hero-heading"
    >
      <div className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full bg-blush blur-3xl opacity-60 glow-pulse" aria-hidden="true" />
      <div className="absolute -bottom-24 -right-10 w-[520px] h-[520px] rounded-full bg-champagne blur-3xl opacity-50 glow-pulse" aria-hidden="true" />

      <div className="container-luxe relative grid lg:grid-cols-12 gap-12 lg:gap-10 items-center pt-12 lg:pt-20 pb-20 lg:pb-28">
        <Reveal className="lg:col-span-6 order-2 lg:order-1">
          <span className="eyebrow">Pink Hearse · Greater Houston, TX</span>
          <h1 id="hero-heading" className="display-xl font-display mt-5">
            Celebrating
            <span className="block font-script text-rose-ink italic leading-[1.05] mt-1">
              Individuality
            </span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-muted-ink max-w-xl leading-relaxed">
            Honoring unique lives with respect &amp; care, a luxury pink hearse
            experience for families who believe every farewell should feel as
            personal as the person it honors.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/contact" className="btn-pill btn-primary" data-testid="hero-contact-btn">
              Contact Us <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <a href={site.emailHref} className="btn-pill btn-ghost" data-testid="hero-email-btn">
              <Mail className="w-4 h-4" strokeWidth={1.5} /> Email for Availability
            </a>
            <Link to="/services" className="btn-pill btn-ivory" data-testid="hero-info-btn">
              Request Information
            </Link>
          </div>

          <div className="mt-12 flex items-center gap-5">
            <span className="hairline" aria-hidden="true" />
            <p className="font-script text-2xl text-rose-ink">
              Because every life deserves a beautiful farewell.
            </p>
          </div>
        </Reveal>

        <div className="lg:col-span-6 order-1 lg:order-2 relative">
          <Reveal className="relative">
            <div className="relative aspect-[5/6] rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-20px_rgba(201,122,136,0.45)] img-zoom">
              <div className="absolute inset-0 kenburns">
                <img
                  src={site.images.hero}
                  alt="Driver in elegant pink suit attire holding an Eternal Care card, a symbol of compassion and individuality"
                  className="w-full h-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-rose-deep/15 via-transparent to-transparent" aria-hidden="true" />
              <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-white/85 backdrop-blur-md p-5 border border-line">
                <p className="font-display text-xl text-charcoal leading-tight">
                  “A symbol of compassion, individuality and the beauty of being uniquely you.”
                </p>
              </div>
            </div>

            <div className="absolute -top-6 -right-2 lg:-right-8 w-32 h-32 rounded-full bg-rose grid place-items-center text-white text-center font-script text-xl shadow-xl float-soft">
              Be Proud.<br />Be You.
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function AboutPreview() {
  return (
    <section className="section bg-ivory">
      <div className="container-luxe grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6">
          <div className="relative">
            <div className="relative aspect-[4/5] rounded-[1.75rem] overflow-hidden shadow-[0_30px_60px_-30px_rgba(42,34,38,0.3)] img-zoom">
              <img
                src={site.images.flowers1}
                alt="Soft pink bouquet wrapped in pastel ribbon, Eternal Care florals"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="hidden md:block absolute -bottom-8 -left-8 w-56 rounded-2xl bg-white p-5 shadow-2xl border border-line">
              <p className="eyebrow">Established with Heart</p>
              <p className="font-display text-2xl mt-1">Greater Houston, TX</p>
              <p className="text-sm text-muted-ink mt-1">Serving families with quiet, dignified care.</p>
            </div>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-6">
          <span className="eyebrow">About Eternal Care</span>
          <h2 className="display-lg font-display mt-4">
            A softer, more personal way to say goodbye.
          </h2>
          <p className="mt-6 text-lg text-muted-ink leading-relaxed">
            Eternal Care is a luxury funeral transportation service rooted in
            compassion and individuality. Our signature pink hearse, drivers in
            elegant pink attire, and family-centered approach create farewells
            that feel beautiful, never cold, never corporate.
          </p>
          <p className="mt-4 text-lg text-muted-ink leading-relaxed">
            We support families with empathy, understanding and quiet competence,
            holding the details so you can hold what matters.
          </p>
          <div className="mt-8">
            <Link to="/about" className="btn-pill btn-ghost" data-testid="about-preview-link">
              Our Story <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Highlights() {
  return (
    <section className="section bg-soft-blush relative overflow-hidden">
      <div className="absolute -top-20 right-0 w-[500px] h-[500px] rounded-full bg-rose/15 blur-3xl" aria-hidden="true" />
      <div className="container-luxe relative">
        <Reveal className="max-w-2xl">
          <span className="eyebrow">What we hold for you</span>
          <h2 className="display-lg font-display mt-3">The Eternal Care promise</h2>
          <p className="mt-4 text-muted-ink text-lg">
            Five quiet commitments behind every farewell we shape.
          </p>
        </Reveal>

        <Reveal stagger className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {site.highlights.map((h) => {
            const Icon = iconMap[h.icon] || Heart;
            return (
              <article
                key={h.title}
                className="group relative bg-white rounded-2xl p-7 border border-line transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(201,122,136,0.35)]"
                data-testid={`highlight-${h.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="w-12 h-12 rounded-full bg-blush grid place-items-center group-hover:bg-rose group-hover:text-white transition-colors">
                  <Icon className="w-5 h-5 text-rose-ink group-hover:text-white transition-colors" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-2xl mt-5 leading-snug">{h.title}</h3>
                <p className="mt-2 text-sm text-muted-ink leading-relaxed">{h.blurb}</p>
              </article>
            );
          })}
        </Reveal>
      </div>
    </section>
  );
}

function QuoteBanner() {
  return (
    <section className="relative bg-charcoal text-white overflow-hidden">
      <div className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full bg-rose/30 blur-3xl" aria-hidden="true" />
      <div className="absolute -bottom-24 -right-10 w-[460px] h-[460px] rounded-full bg-blush/15 blur-3xl" aria-hidden="true" />

      <div className="container-luxe relative py-24 lg:py-32 text-center">
        <Reveal>
          <Quote className="w-10 h-10 text-rose-soft mx-auto" strokeWidth={1.2} />
          <p className="font-display italic display-md mt-6 max-w-4xl mx-auto leading-tight">
            Life is unique.
            <span className="block font-script not-italic text-rose-soft text-5xl md:text-6xl mt-3">
              The goodbye should be too.
            </span>
          </p>
          <span className="hairline mt-10 inline-block" aria-hidden="true" />
        </Reveal>
      </div>
    </section>
  );
}

function ServicesPreview() {
  return (
    <section className="section bg-ivory">
      <div className="container-luxe">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-xl">
            <span className="eyebrow">What we do</span>
            <h2 className="display-lg font-display mt-3">Heartfelt transportation services</h2>
          </div>
          <p className="text-muted-ink max-w-md">
            Personalized arrangements that reflect the life and legacy of your
            loved one, handled with grace and quiet luxury.
          </p>
        </Reveal>

        <Reveal stagger className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {site.services.slice(0, 6).map((s) => {
            const Icon = iconMap[s.icon] || Heart;
            return (
              <Link
                key={s.key}
                to="/services"
                data-testid={`service-card-${s.key}`}
                className="group relative bg-white rounded-2xl p-8 border border-line transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_60px_-25px_rgba(201,122,136,0.45)] hover:border-rose"
              >
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 rounded-full bg-blush grid place-items-center group-hover:bg-rose transition-colors">
                    <Icon className="w-5 h-5 text-rose-ink group-hover:text-white transition-colors" strokeWidth={1.5} />
                  </div>
                  <ChevronRight className="w-5 h-5 text-rose-ink opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-500" />
                </div>
                <h3 className="font-display text-2xl mt-6 leading-tight">{s.title}</h3>
                <p className="mt-3 text-muted-ink leading-relaxed">{s.blurb}</p>
              </Link>
            );
          })}
        </Reveal>

        <div className="mt-12 flex justify-center">
          <Link to="/services" className="btn-pill btn-primary" data-testid="services-preview-cta">
            View All Services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function WhyUs() {
  return (
    <section className="section bg-cream">
      <div className="container-luxe grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <span className="eyebrow">Why Families Choose Us</span>
          <h2 className="display-lg font-display mt-3">A farewell as singular as the life it honors.</h2>
          <p className="mt-6 text-muted-ink text-lg leading-relaxed">
            Families come to Eternal Care because they want more than transportation,
            they want a moment that feels like their loved one. We deliver that with
            warmth, taste and quiet, attentive presence.
          </p>
          <div className="mt-8">
            <Link to="/about" className="btn-pill btn-ghost" data-testid="why-us-learn-more">
              Learn More <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal stagger className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
          {site.whyUs.map((item) => (
            <article
              key={item.title}
              className="bg-white rounded-2xl p-7 border border-line hover:border-rose transition-colors duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(201,122,136,0.3)]"
            >
              <h3 className="font-display text-2xl">{item.title}</h3>
              <p className="text-sm text-muted-ink mt-2 leading-relaxed">{item.body}</p>
            </article>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

function ServiceAreas() {
  return (
    <section className="section bg-ivory">
      <div className="container-luxe">
        <Reveal className="max-w-3xl mx-auto text-center">
          <span className="eyebrow">Where we serve</span>
          <h2 className="display-lg font-display mt-3">Greater Houston &amp; Surrounding Texas</h2>
          <p className="mt-4 text-muted-ink text-lg">
            Proudly serving families across Houston and the communities that surround it.
          </p>
        </Reveal>

        <Reveal stagger className="mt-12 flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
          {site.serviceAreas.map((a) => (
            <span
              key={a}
              className="px-5 py-2.5 rounded-full bg-white border border-line text-sm tracking-wide hover:bg-blush hover:border-rose transition-colors"
              data-testid={`area-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {a}
            </span>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

function FloralTribute() {
  return (
    <section className="relative overflow-hidden">
      <div className="relative h-[560px] md:h-[640px]">
        <img
          src={site.images.flowers2}
          alt="Vibrant pink and white memorial floral arrangement"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        {/* Strong dark wash for legibility, layered with a soft side gradient */}
        <div className="absolute inset-0 bg-charcoal/55" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-charcoal/55 to-transparent" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 to-transparent" aria-hidden="true" />

        <div className="relative container-luxe h-full flex items-center">
          <Reveal className="max-w-2xl text-white">
            <span className="eyebrow !text-rose-soft drop-shadow-[0_1px_4px_rgba(0,0,0,0.5)]">A floral tribute</span>
            <h2 className="display-lg font-display mt-4 text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)]">
              Every farewell, in full bloom.
            </h2>
            <p className="mt-6 text-white text-lg md:text-xl leading-relaxed drop-shadow-[0_1px_8px_rgba(0,0,0,0.5)]">
              We coordinate with your florist or curate arrangements ourselves,
              ensuring the hearse, the procession, and the moment are wrapped in
              the colors and blooms that meant the most to your loved one.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}


function FaqPreview() {
  const items = site.faqs.slice(0, 4);
  return (
    <section className="section bg-cream">
      <div className="container-luxe grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4">
          <span className="eyebrow">Common Questions</span>
          <h2 className="display-lg font-display mt-3">Answered with care</h2>
          <p className="mt-4 text-muted-ink">A few things families often ask before reaching out.</p>
          <div className="mt-8">
            <Link to="/faq" className="btn-pill btn-ghost" data-testid="faq-preview-cta">
              All Questions <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal stagger className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
          {items.map((f, i) => (
            <article key={i} className="bg-white rounded-2xl p-6 border border-line" data-testid={`faq-preview-${i}`}>
              <h3 className="font-display text-xl">{f.q}</h3>
              <p className="mt-2 text-sm text-muted-ink leading-relaxed">{f.a}</p>
            </article>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

function ContactCta() {
  return (
    <section className="section bg-ivory">
      <div className="container-luxe">
        <Reveal>
          <div className="relative rounded-[2rem] overflow-hidden p-10 md:p-16 bg-soft-blush border border-line">
            <div className="absolute -top-20 -right-10 w-72 h-72 rounded-full bg-rose/20 blur-3xl" aria-hidden="true" />
            <div className="relative grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-7">
                <span className="eyebrow">Reach Out</span>
                <h2 className="display-lg font-display mt-3">
                  Let us help you craft a goodbye that feels like them.
                </h2>
                <p className="mt-4 text-muted-ink text-lg max-w-xl">
                  For pricing, booking, and availability, please contact us directly via email or phone.
                </p>
              </div>
              <div className="md:col-span-5 flex flex-col gap-4">
                <a href={site.phoneHref} className="btn-pill btn-primary justify-center" data-testid="cta-phone-btn">
                  <Phone className="w-4 h-4" /> {site.phone}
                </a>
                <a href={site.emailHref} className="btn-pill btn-ghost justify-center bg-white" data-testid="cta-email-btn">
                  <Mail className="w-4 h-4" /> {site.email}
                </a>
                <Link to="/contact" className="btn-pill btn-ivory justify-center" data-testid="cta-form-link">
                  <MapPin className="w-4 h-4" /> Send a Message
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        title="Eternal Care | Pink Hearse Funeral Transportation in Houston, TX"
        description="Luxury pink hearse funeral transportation in Greater Houston. Honoring lives, celebrating individuality, dignified, personalized and heartfelt service."
        path="/"
      />
      <Hero />
      <AboutPreview />
      <Highlights />
      <QuoteBanner />
      <ServicesPreview />
      <WhyUs />
      <ServiceAreas />
      <FloralTribute />
      <FaqPreview />
      <ContactCta />
    </>
  );
}
